<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Team`.trim()" icon="mdi-account-group-outline" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-shield-outline</v-icon>Details
                </div>
                <v-row align="center">
                    <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                            v-model="team.name"
                            outlined
                            dense
                            hide-details="auto"
                            label="Name"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                            v-model="team.nickName"
                            outlined
                            dense
                            hide-details="auto"
                            label="Nickname"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <!-- a team can play in more than one league -->
                        <v-autocomplete
                            v-model="team.leagues"
                            outlined
                            dense
                            hide-details="auto"
                            :items="leagues"
                            item-text="title"
                            item-value="id"
                            menu-props="auto"
                            label="Leagues"
                            multiple
                            small-chips
                            :deletable-chips="!readOnly"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <v-autocomplete
                            v-model="team.cupLeague"
                            outlined
                            dense
                            hide-details="auto"
                            :items="cups"
                            item-text="title"
                            item-value="id"
                            menu-props="auto"
                            label="Cup League"
                            :clearable="!readOnly"
                            return-object
                        />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :value="team.color"
                                    label="Team Color"
                                    append-icon="mdi-menu-down"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <template v-slot:prepend>
                                        <v-icon large :color="team.color">
                                            mdi-palette
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-row class="py-10">
                                <v-col cols="12" align="center">
                                    <v-color-picker
                                        v-model="team.color"
                                        dot-size="23"
                                        hide-inputs
                                        flat
                                        swatches-max-height="200"
                                    />
                                </v-col>
                            </v-row>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :value="team.brandColor"
                                    label="Match Color"
                                    append-icon="mdi-menu-down"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <template v-slot:prepend>
                                        <v-icon large :color="team.brandColor">
                                            mdi-palette-swatch-outline
                                        </v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-row class="py-10">
                                <v-col cols="12" align="center">
                                    <v-color-picker
                                        v-model="team.brandColor"
                                        dot-size="23"
                                        hide-inputs
                                        flat
                                        swatches-max-height="200"
                                    />
                                </v-col>
                            </v-row>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <v-switch
                            v-model="team.active"
                            class="mt-0 pt-0"
                            color="primary"
                            hide-details
                            inset
                            :label="team.active ? 'Active' : 'Not Active'"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-account-group-outline</v-icon>Players
                    <v-chip v-if="sortedPlayers && sortedPlayers.length" x-small dark color="black" class="ml-1">{{ sortedPlayers.length }}</v-chip>
                </div>
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            v-model="sortedPlayers"
                            outlined
                            dense
                            hide-details="auto"
                            :items="sortedPlayers"
                            :item-text="getPlayerText"
                            :loading="loading"
                            :search-input.sync="search"
                            label="Players"
                            multiple
                            chips
                            clearable
                            return-object
                        >
                            <template v-slot:selection="data">
                                <v-chip
                                    v-bind="data.attrs"
                                    :input-value="data.selected"
                                    close
                                    @click.stop="openDialog(data.item)"
                                    @click:close="remove(data.item)"
                                >
                                    <v-avatar left>
                                        <v-img :src="googleApiHost + '/' + data.item.image" />
                                    </v-avatar>
                                    {{ `${data.item.jersey} ${data.item.name} ${data.item.surname || ''}`.trim() }}
                                    <span class="primary--text">
                                        {{ '/ Active:' }}
                                        <v-icon :color="data.item.active ? 'green' : 'red'">
                                            {{ data.item.active ? 'mdi-check-outline' : 'mdi-close-outline' }}
                                        </v-icon>
                                        {{ '/ Doctor:' }}
                                        <v-icon :color="data.item.doctorCheck ? 'green' : 'red'">
                                            {{ data.item.doctorCheck ? 'mdi-check-outline' : 'mdi-close-outline' }}
                                        </v-icon>
                                    </span>
                                </v-chip>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-cash-check</v-icon>Subscription per season
                    <v-chip v-if="currentSubscription" x-small dark class="ml-2" :color="currentSubscription.paid ? 'green darken-1' : 'red darken-1'">
                        {{ currentSubscription.paid ? 'Paid' : 'Unpaid' }} · {{ currentSubscription.seasonName }}
                    </v-chip>
                </div>
                <div v-if="isAdmin && !readOnly" class="text-caption grey--text text--darken-1 mb-3">
                    One row per season the team plays: <b>Paid</b> or <b>Unpaid</b>, and the amount if you want to keep it (optional).
                    A season without a row counts as unpaid.
                </div>
                <amount-rows
                    v-model="team.subscriptions"
                    :items="seasons"
                    :item-text="seasonLabel"
                    key-field="seasonId"
                    label-field="seasonName"
                    amount-field="amount"
                    amount-label="Amount (optional)"
                    mode-field="paid"
                    :modes="subscriptionModes"
                    amount-optional
                    :hide-amount="!isAdmin"
                    select-label="Season"
                    add-label="Add season"
                    empty-text="No subscription recorded yet."
                    icon="mdi-calendar"
                    :readonly="readOnly || !isAdmin"
                />

                <div class="form-section-title">
                    <v-icon small>mdi-image-outline</v-icon>Logo & image
                </div>
                <v-row>
                    <v-col cols="12" sm="6" lg="4">
                        <image-upload
                            v-model="logo"
                            :src="logoPath"
                            label="Team Logo"
                            :readonly="readOnly"
                            required
                            @change="changeLogo"
                            @clear="clearLogo"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                        <image-upload
                            v-model="image"
                            :src="imagePath"
                            label="Team Image"
                            :readonly="readOnly"
                            required
                            @change="changeImage"
                            @clear="clearImage"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
        <v-dialog
            v-model="dialog"
            width="90%"
        >
            <v-card>
                <player-form :prop-id="selectedPlayerId" @updatePlayer="updatePlayer" />
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import teamService from '@/services/team'
    import Alert from '@/components/Alert'
    import leagueService from '@/services/league'
    import cupService from '@/services/cup'
    import playerService from '@/services/player'
    import PlayerForm from '@/forms/PlayerForm'
    import AmountRows from '@/components/AmountRows'
    import seasonService from '@/services/season'
    import { seasonLabel, sortSeasons } from '@/utils/season'

    export default {
        name: 'TeamForm',
        components: { Alert, PlayerForm, PageHeader, ImageUpload, AmountRows },
        data: () => {
            return {
                selectedPlayerId: null,
                showAlert: false,
                dialog: false,
                message: null,
                type: null,
                mode: null,
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                googleApiHost: 'https://storage.googleapis.com',
                team: {
                    color: '#000000',
                    brandColor: '#000000',
                    leagues: [],
                    players: [],
                    subscriptions: []
                },
                seasons: [],
                subscriptionModes: [
                    { value: false, text: 'Unpaid' },
                    { value: true, text: 'Paid' }
                ],
                leagues: [],
                cups: [],
                teamId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                image: {
                    name: 'Image file'
                },
                imageChanged: false,
                imagePath: '',
                logo: {
                    name: 'Image file'
                },
                logoChanged: false,
                logoPath: '',
                mandatoryField: [
                    v => !!v || 'Field is required'
                ],
                players: [],
                search: null
            }
        },
        computed: {
            isAdmin() {
                return this.$store.getters.isAdmin
            },
            // the subscription of the season running today, shown next to the section title
            currentSubscription() {
                const now = Date.now()
                const season = this.seasons.find(item => new Date(item.startDate) <= now && now <= new Date(item.endDate))
                if (!season) return null
                const row = (this.team.subscriptions || []).find(item => item.seasonId === season.id)
                return { paid: !!(row && row.paid), seasonName: seasonLabel(season) }
            },
            sortedPlayers() {
                // a team without players (or a response without them) must not break the form
                return [...(this.team.players || [])].sort((a, b) => {
                    if (a.active === b.active) return 0
                    return a.active ? -1 : 1
                })
            }
        },
        watch: {
            search() {
                if (this.search.length > 0) {
                    this.fetchFilteredPlayers()
                }
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            seasonService.getSeasons().then(resp => {
                this.seasons = sortSeasons(resp.data.content)
            })
            this.teamId = this.$route.params.teamId
            if (this.teamId) {
                teamService.getTeam(this.teamId).then(resp => {
                    this.team = { ...resp.data, players: resp.data.players || [], subscriptions: resp.data.subscriptions || [] }
                    this.players = this.team.players
                    if (this.team.image) {
                        this.imagePath = this.googleApiHost + '/' + this.team.image
                    }
                    if (this.team.logo) {
                        this.logoPath = this.googleApiHost + '/' + this.team.logo
                    }
                    if (!this.team.color) {
                        this.team.color = '#000000'
                    }
                    if (!this.team.brandColor) {
                        this.team.brandColor = '#000000'
                    }
                    if (!this.team.leagues) {
                        this.team.leagues = this.team.league ? [this.team.league] : []
                    }
                })
            } else {
                this.fetchAllPlayers()
            }
            this.getAllLeagues()
            cupService.getAllCups().then(resp => {
                this.cups = resp.data
            })
        },
        methods: {
            seasonLabel,
            updatePlayer() {
                this.dialog = false
                teamService.getTeam(this.teamId).then(resp => {
                    this.team = { ...resp.data, players: resp.data.players || [], subscriptions: resp.data.subscriptions || [] }
                    this.players = this.team.players
                    if (this.team.image) {
                        this.imagePath = this.googleApiHost + '/' + this.team.image
                    }
                    if (this.team.logo) {
                        this.logoPath = this.googleApiHost + '/' + this.team.logo
                    }
                    if (!this.team.color) {
                        this.team.color = '#000000'
                    }
                    if (!this.team.brandColor) {
                        this.team.brandColor = '#000000'
                    }
                    if (!this.team.leagues) {
                        this.team.leagues = this.team.league ? [this.team.league] : []
                    }
                })
            },
            openDialog(data) {
                this.dialog = true
                this.selectedPlayerId = data.id
            },
            remove(item) {
                const index = this.team.players.indexOf(item)
                if (index >= 0) {
                    this.team.players.splice(index, 1)
                }
            },
            getPlayerText(item) {
                return `#${item.jersey} ${item.name} ${item.surname || ''}`.trim()
            },
            clearImage() {
                this.imagePath = ''
                this.team.image = null
            },
            clearLogo() {
                this.logo = ''
                this.team.logo = null
            },
            changeImage() {
                this.imageChanged = true
                if (this.image) {
                    this.imagePath = URL.createObjectURL(this.image)
                }
            },
            changeLogo() {
                this.logoChanged = true
                if (this.logo) {
                    this.logoPath = URL.createObjectURL(this.logo)
                }
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                // a subscription row needs its season
                return (this.team.subscriptions || []).some(row => !row.seasonId)
            },
            setStartDate(date) {
                this.startDate = date
            },
            setEndDate(date) {
                this.endDate = date
            },
            onEdit() {
                this.$router.push(`/teams/${this.teamId}/edit`)
            },
            getAllLeagues() {
                leagueService.getAllLeagues().then(resp => {
                    this.leagues = resp.data
                })
            },
            createTeam() {
                teamService.createTeam(this.team).then(teamResponse => {
                    this.teamId = teamResponse.data.content
                    this.loading = false
                    this.$router.push(`/teams`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editTeam() {
                teamService.updateTeam(this.teamId, this.team).then(teamResponse => {
                    if (teamResponse.id) {
                        this.teamId = teamResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/teams`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            async onSubmit() {
                this.loading = true
                // an empty amount is no amount; only admins change subscriptions (the backend ignores the rest)
                this.team.subscriptions = (this.team.subscriptions || []).map(row => ({
                    ...row,
                    paid: !!row.paid,
                    amount: row.amount === '' || row.amount === null || row.amount === undefined ? null : Number(row.amount)
                }))
                const uploadImageResponse = await this.uploadImage()
                const uploadLogoResponse = await this.uploadLogo()
                const imageUrl = uploadImageResponse?.data.content || null
                if (imageUrl) {
                    this.team.image = imageUrl
                }
                const logoUrl = uploadLogoResponse?.data.content || null
                if (logoUrl) {
                    this.team.logo = logoUrl
                }
                // the leagues are sent as a list; the legacy single `league` would add its league back
                this.team.league = null
                if (this.mode === 'edit') {
                    this.editTeam()
                } else if (this.mode === 'create') {
                    this.createTeam()
                } else {
                    this.createTeam()
                }
            },
            uploadImage() {
                if (!this.imageChanged || this.image == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.image)
                return teamService.uploadFile(formData)
            },
            uploadLogo() {
                if (!this.logoChanged || this.logo == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.logo)
                return teamService.uploadFile(formData)
            },
            fetchFilteredPlayers() {
                this.loading = true
                playerService.getFilteredPlayers({ q: this.search }).then(resp => {
                    this.players = [...new Set(this.players.concat(resp.data.content))]
                    this.loading = false
                })
            },
            fetchAllPlayers() {
                this.loading = true
                playerService.getPlayers().then(resp => {
                    this.players = resp.data.content
                    this.loading = false
                })
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.v-menu__content{
    background-color: white!important;
    overflow-y: hidden!important;
}
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
